// ============================================================================
// AuthService - Authentication State & Login Flow
// ============================================================================
// Manages login, 2FA verification, logout, and session check on app startup.
// Replaces V1's login controller logic and session cookie checks.
// Exposes authenticated$ observable and impersonation flag (sales rep as customer).
// ============================================================================

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';

export interface LoginResponse {
  success: boolean;
  requires2FA: boolean;
  requirePasswordChange: boolean;
  token?: string;
  error?: string;
}

@Injectable()
export class AuthService {
  private authenticatedSubj = new BehaviorSubject<boolean>(false);
  authenticated$ = this.authenticatedSubj.asObservable();

  /** True when a sales rep is viewing the portal as a customer (on-prem only) */
  impersonating = false;

  get isAuthenticated(): boolean {
    return this.authenticatedSubj.getValue();
  }

  constructor(private http: HttpClient, private router: Router) {}

  login(username: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>('/api/auth/login', { username, password }).pipe(
      tap(res => {
        if (res.success && !res.requires2FA) {
          this.impersonating = false;
          this.authenticatedSubj.next(true);
        }
      })
    );
  }

  verifyCode(code: string, username: string, trustDevice: boolean): Observable<LoginResponse> {
    return this.http.post<LoginResponse>('/api/auth/verify-code', { code, username, trustDevice }).pipe(
      tap(res => {
        if (res.success) {
          this.impersonating = false;
          this.authenticatedSubj.next(true);
        }
      })
    );
  }

  // Called on app startup to check if existing cookie is still valid
  checkAuth(): Observable<any> {
    return this.http.get<any>('/api/auth/check').pipe(
      tap(
        res => {
          this.authenticatedSubj.next(res.authenticated);
          this.impersonating = res.impersonating === true;
        },
        () => this.authenticatedSubj.next(false)
      )
    );
  }

  logout(): void {
    this.http.post('/api/auth/logout', {}).subscribe(() => {
      this.impersonating = false;
      this.authenticatedSubj.next(false);
      this.router.navigate(['/login']);
    });
  }
}
